// Backend availability probe used by the maintenance gate.
//
// The backend is shut down on weekends to save cost while the frontend stays
// up on Firebase. We detect that by hitting the unauthenticated /health
// endpoint (served at the root, not under /api) with a short timeout — a
// stopped EC2 instance never answers, so without a timeout the probe would
// hang for the browser's full connect timeout.

const HEALTH_URL = `${process.env.REACT_APP_BACKEND_URL}/health`;
const PROBE_TIMEOUT_MS = 8000;

// Fired by the axios interceptor when a request fails in a way that looks
// like the backend is gone (no response / 502 / 503 / 504). The gate then
// re-probes /health before switching to the maintenance page, so one flaky
// request doesn't kick a user out.
export const BACKEND_UNREACHABLE_EVENT = "backend-unreachable";

export function isBackendDownError(err) {
  if (!err || err.code === "ERR_CANCELED") return false;
  if (!err.response) return true; // network error / connection refused / CORS on proxy error page
  return [502, 503, 504].includes(err.response.status);
}

export function notifyBackendUnreachable() {
  window.dispatchEvent(new Event(BACKEND_UNREACHABLE_EVENT));
}

// Resolves true when the backend is up and can reach its database.
export async function probeBackend() {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);
  try {
    const res = await fetch(HEALTH_URL, { signal: controller.signal, cache: "no-store" });
    if (!res.ok) return false;
    const body = await res.json().catch(() => null);
    return body?.status !== "degraded";
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}
